import { Control, UseFormReturn } from "react-hook-form"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Settings, Gamepad2, Trophy } from "lucide-react"

export const storeClassifications = [
  { id: 1, name: "Pequeña" },
  { id: 2, name: "Intermedia" },
  { id: 3, name: "Grande" },
  { id: 4, name: "Juega en casa" },
]

export const storeLevels = [
  { id: 1, name: "Organizador" },
  { id: 2, name: "Tienda Oficial" },
  { id: 3, name: "Tienda Oficial Klu" },
  { id: 4, name: "Tienda Legendaria" },
]

interface Game {
  id: number
  name: string
  isActive?: boolean
}

interface TournamentType {
  id: number
  name: string
  private?: boolean
  storeTournaments?: number
}

interface StoreAdminSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  games: Game[]
  tournamentTypes: TournamentType[]
  tournamentTypeQuantities: Record<number, number>
  setTournamentTypeQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>
  showActiveSwitch?: boolean
  showClassification?: boolean
  showLevel?: boolean
  showGames?: boolean
  showTournamentTypes?: boolean
}

export function StoreAdminSection({
  control,
  games,
  tournamentTypes,
  tournamentTypeQuantities,
  setTournamentTypeQuantities,
  showActiveSwitch = true,
  showClassification = true,
  showLevel = true,
  showGames = true,
  showTournamentTypes = true,
}: StoreAdminSectionProps) {
  return (
    <Card className="border-orange-200 dark:border-orange-800">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5 text-orange-600" />
          <CardTitle className="text-orange-700 dark:text-orange-400">
            Configuración de Administrador
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {showActiveSwitch && (
          <FormField
            control={control}
            name="active"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <FormLabel>Estado de la Tienda</FormLabel>
                  <p className="text-sm text-muted-foreground">Activar o desactivar la tienda</p>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {showClassification && (
            <FormField
              control={control}
              name="clasificationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Clasificación</FormLabel>
                  <Select
                    value={field.value?.toString()}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar clasificación" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {storeClassifications.map((classification) => (
                        <SelectItem key={classification.id} value={classification.id.toString()}>
                          {classification.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {showLevel && (
            <FormField
              control={control}
              name="levelId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nivel de Tienda</FormLabel>
                  <Select
                    value={field.value?.toString()}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar nivel" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {storeLevels.map((level) => (
                        <SelectItem key={level.id} value={level.id.toString()}>
                          {level.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Games Section */}
        {showGames && games.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Gamepad2 className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold">Juegos Disponibles</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {games.map((game) => (
                <FormField
                  key={game.id}
                  control={control}
                  name="storeGames"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(game.id)}
                          onCheckedChange={(checked) => {
                            const current = field.value || []
                            if (checked) {
                              field.onChange([...current, game.id])
                            } else {
                              field.onChange(current.filter((id: number) => id !== game.id))
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal flex items-center justify-between">
                        <span
                          className={`text-xs mr-2 font-medium px-2 py-1 rounded-full ${
                            game.isActive
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                          }`}
                        >
                          {game.isActive ? "Activo" : "Inactivo"}
                        </span>
                        <span>{game.name}</span>
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        )}

        {/* Tournament Types Section */}
        {showTournamentTypes && tournamentTypes.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold">Tipos de Torneos</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tournamentTypes
                .filter((x) => !x.private)
                .map((type) => {
                  const storeTournaments = type.storeTournaments || 0
                  return (
                    <div key={type.id} className="flex items-center space-x-3 justify-between">
                      <span className="text-sm font-medium flex-1 flex flex-col sm:flex-row gap-1">
                        <span>{type.name}</span>
                        <span className="text-muted-foreground px-2 py-1 rounded">
                          (mensuales: {storeTournaments})
                        </span>
                      </span>
                      <Input type="number" disabled value={storeTournaments} className="w-16" />
                      <Input
                        type="number"
                        min="0"
                        className="w-20"
                        value={tournamentTypeQuantities[type.id] || 0}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0
                          setTournamentTypeQuantities((prev) => ({
                            ...prev,
                            [type.id]: value,
                          }))
                        }}
                      />
                      <Input
                        type="number"
                        disabled
                        value={storeTournaments + (tournamentTypeQuantities[type.id] || 0)}
                        className="w-16"
                      />
                    </div>
                  )
                })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
